import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { TogglUserDetails, TogglLoginResult, MatchTogglUsersResult, MatchedTogglJobCounts, LoadTimeEntriesResult, UpdateTimeEntryRatesResult, TimeCalendarResult, TogglTimerResult, TogglRunningJob, RecentTimerDescriptionsResult, RecentTimerEvent } from "./toggl.types";
import { BehaviorSubject, Observable, Subscription, of, timer } from "rxjs";
import { map, tap, take } from "rxjs/operators";
import { ResultBase } from "@intranet/core/types";
import * as moment from "moment";

export interface TogglJob {
    id: number;
    title: string;
    jobNumber: string;
    description: string;
}

@Injectable({
    providedIn: 'root',
})export class TogglService {
    private readonly togglEnabled = false;
    private toggluser: TogglUserDetails | null = null;
    togglApiUrl: string = 'https://api.track.toggl.com/api/v9/';
    timerSubscription: Subscription | null = null;

    private _togglRunningJob$: BehaviorSubject<TogglRunningJob | null> = new BehaviorSubject(null);

    isPolling: boolean = false;
    lastPolledAt: moment.Moment | null = null;
    isTogglTimerRunning: boolean = false;
    private lastJob: TogglJob | null = null;
    private _isLoggedIn = new BehaviorSubject<boolean>(false);
    public isLoggedIn$ = this._isLoggedIn.asObservable();

    constructor(
        private _http: HttpClient,
        @Inject('BASE_URL') private originUrl: string
    ) {
          if (!this.togglEnabled) {
              this.clearAuthAndStop();
              return;
          }
          this.initialiseToggl();
    }

    private clearAuthAndStop(): void {
        this.toggluser = null;
        try { localStorage.removeItem('toggluser'); } catch {}
        this._isLoggedIn.next(false);
        this.stopTimerSubscription();
        this._togglRunningJob$.next(null);
        this.isTogglTimerRunning = false;
    }

    get togglRunningJob$(): Observable<TogglRunningJob | null> {
        return this._togglRunningJob$.asObservable();
    }

    initialiseToggl() {
      if (!this.togglEnabled) { this.clearAuthAndStop(); return; }

      const json = localStorage.getItem('toggluser');
      if (!json) return;

      const candidate: TogglUserDetails = JSON.parse(json);
      this._http
          .get<ResultBase>(`${this.originUrl}api/toggl/check-key`, { params: { apikey: candidate.api_token } })
          .subscribe(r => {
              if (!r.success) {
                  this.toggluser = null;
                  localStorage.removeItem('toggluser');
              } else {
                  this.toggluser = candidate;
                  this._isLoggedIn.next(true);          // ← only now
                  this.restartTimerSubscription();
              }
          });
    }

    getLastJob(): TogglJob | null {
        return this.lastJob;
    }

    restartTimerSubscription() {
        if (!this.togglEnabled) { this.stopTimerSubscription(); return; }

        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
        this.timerSubscription = timer(0,1000)
            .subscribe({
                next: () => {
                    if (!this.isPolling && this.isLoggedIn() &&(!this.lastPolledAt || moment().diff(this.lastPolledAt, 'seconds') > 60)) {
                        this.isPolling = true;
                        this.getTogglRunningTimer().subscribe({
                            next: (result: TogglTimerResult) => {
                                this.lastPolledAt = moment();
                                if (!result.success) {
                                  this.isTogglTimerRunning = false;
                                  this._togglRunningJob$.next(null);
                                } else {
                                    const job = result.runningJob;
                                    job.startTime = moment(job.startTime);
                                    this._togglRunningJob$.next(job);
                                    this.isTogglTimerRunning = true;

                                    // ← record it for later, using the correct property names
                                    this.lastJob = {
                                      id:        job.jobId,
                                      title:     job.jobTitle,
                                      jobNumber: job.jobNumber.toString(),
                                      description: job.description || ''
                                    };
                                }
                                this.isPolling = false;
                            },
                            error: (error) => {
                                this.isPolling = false;
                            }
                        });
                    }
                }
            });
    }

    stopTimerSubscription() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }
    }

    resumeLastJob(): Observable<TogglTimerResult> {
        if (!this.lastJob) {
            return of({ success: false, error: 'No previous job to resume', runningJob: null });
        }
        return this.startTimerForJob(this.lastJob.id);
    }

    isLoggedIn() {
        return this.togglEnabled && !!this.toggluser;
    }

    login(username: string, password: string): Observable<{ result: boolean, message: string }> {
        if (!this.togglEnabled) return of({ result: false, message: 'Toggl is disabled' });

        return this._http
          .post<TogglLoginResult>(`${this.originUrl}api/toggl/login`, { username, password })
          .pipe(
              tap(r => {
                  if (r.success) {
                    this.toggluser = r.user;
                    localStorage.setItem('toggluser', JSON.stringify(r.user));
                    this._isLoggedIn.next(true);

                    this.restartTimerSubscription();

                    this.getTogglRunningTimer()
                        .pipe(take(1))
                        .subscribe(res => {
                          if (res.success && res.runningJob) {
                              const job = res.runningJob;
                              job.startTime = moment(job.startTime);
                              this._togglRunningJob$.next(job);
                              this.isTogglTimerRunning = true;
                              this.lastJob = {
                                  id:        job.jobId,
                                  title:     job.jobTitle,
                                  jobNumber: job.jobNumber.toString(),
                                  description: job.description || ''
                              };
                          }
                        });
                  }
              }),
              map(r => ({ result: r.success, message: r.error }))
          );
  }

  private getTogglRunningTimer() : Observable<TogglTimerResult> {
      if (!this.togglEnabled) return of({ success: false, error: 'Toggl is disabled', runningJob: null });

      if (!this.toggluser) {
          return of({ success: false, error: 'User Not Logged In', runningJob: null });
      }
      return this._http.get<TogglTimerResult>(this.originUrl + 'api/toggl/get-running-timer?apikey=' + this.toggluser.api_token);
  }

  startTimerForJob(jobId: number): Observable<TogglTimerResult> {
      if (!this.togglEnabled) return of({ success: false, error: 'Toggl is disabled', runningJob: null });
      if (!this.toggluser)    return of({ success:false, error:'User Not Logged In', runningJob:null });

      return this._http.post<TogglTimerResult>(
            this.originUrl + 'api/toggl/start-timer-for-job',
            { jobId, workspaceid: this.toggluser.default_workspace_id, apiKey: this.toggluser.api_token }
        ).pipe(
            tap((result) => {
                if (result.success && result.runningJob) {
                    result.runningJob.startTime = moment(result.runningJob.startTime);
                    this._togglRunningJob$.next(result.runningJob);
                    this.isTogglTimerRunning = true;

                    this.lastJob = {
                        id:        result.runningJob.jobId,
                        title:     result.runningJob.jobTitle,
                        jobNumber: result.runningJob.jobNumber.toString(),
                        description: result.runningJob.description || ''
                    };
                } else {
                    console.error('Error starting timer:', result.error);
                    this._togglRunningJob$.next(null);
                    this.isTogglTimerRunning = false;
                }
            })
        );
  }

  updateCurrentTimerDescription(description: string): Observable<TogglTimerResult> {
    if (!this.togglEnabled) return of({ success: false, error: 'Toggl is disabled', runningJob: null });
    if (!this.toggluser)    return of({ success:false, error:'User Not Logged In', runningJob:null });

    return this._http.post<TogglTimerResult>(this.originUrl + 'api/toggl/update-current-timer-description',
        { description: description, workspaceId: this.toggluser.default_workspace_id, apiKey: this.toggluser.api_token }
    ).pipe(
        tap((result) => {
            if (result.success && result.runningJob) {
                result.runningJob.startTime = moment(result.runningJob.startTime);
                this._togglRunningJob$.next(result.runningJob);
                this.isTogglTimerRunning = true;

                this.lastJob = {
                    id:        result.runningJob.jobId,
                    title:     result.runningJob.jobTitle,
                    jobNumber: result.runningJob.jobNumber.toString(),
                    description: result.runningJob.description || ''
                };
            } else {
                console.error('Error updating timer description:', result.error);
            }
        })
    );
  }

  getRecentTimerDescriptions(jobId: number): Observable<RecentTimerDescriptionsResult> {
      if (!this.togglEnabled) return of({ success: true, error: '', descriptions: [] });

      return this._http.post<RecentTimerDescriptionsResult>(this.originUrl + 'api/toggl/get-recent-timer-descriptions',
          { jobId: jobId, apiKey: this.toggluser.api_token }
      );
  }


  getRecentTimerEvents(): Observable<RecentTimerEvent[]> {
      if (!this.togglEnabled) return of([] as RecentTimerEvent[]);
      if (!this.toggluser) return of([]);

      return this._http
        .get<{
            recentEvents: RecentTimerEvent[];
            success: boolean;
            error: string | null;
        }>(
            `${this.originUrl}api/toggl/get-recent-timer-events`,
            { params: { apiKey: this.toggluser!.api_token } }
        )
        .pipe(
            map(response => response.recentEvents)
        );
  }

  startTimerFromEvent(evt: RecentTimerEvent): Observable<TogglTimerResult> {
      if (!this.togglEnabled) return of({ success: false, error: 'Toggl is disabled', runningJob: null });

      return this.startTimerForJob(evt.jobId).pipe(
          tap(res => {
              if (res.success) {
                // once the timer is running, apply the old description
                this.updateCurrentTimerDescription(evt.description).subscribe();
              }
          })
      );
  }

    stopTimer() : Observable<{ success: boolean, error: string }> {
        if (!this.togglEnabled) return of({ success: false, error: 'Toggl is disabled' });
        if (!this.toggluser) return of({ success:false, error:'User Not Logged In' });

        return this._http.post<{ success: boolean, error: string }>(this.originUrl + 'api/toggl/stop-running-timer',
            { "workspaceid": this.toggluser.default_workspace_id, "apiKey": this.toggluser.api_token }
        ).pipe(
            tap((result) => {
                if (result.success) {
                    this._togglRunningJob$.next(null);
                    this.isTogglTimerRunning = false;
                } else {
                    console.error('Error stopping timer:', result.error);
                }

            })
        );
    }

    matchUsers() : Observable<MatchTogglUsersResult> {
        return this._http.get<MatchTogglUsersResult>(this.originUrl + 'api/toggl/match-toggl-users');
    }

    updateUsers() : Observable<MatchTogglUsersResult> {
        return this._http.post<MatchTogglUsersResult>(this.originUrl + 'api/toggl/update-toggl-users', null);
    }

    resetUsers() : Observable<MatchTogglUsersResult> {
        return this._http.post<MatchTogglUsersResult>(this.originUrl + 'api/toggl/reset-toggl-users', null);
    }

    getMatchedJobCounts() : Observable<MatchedTogglJobCounts> {
        return this._http.get<MatchedTogglJobCounts>(this.originUrl + 'api/toggl/get-matched-job-counts');
    }

    updateUnmatchedJobs() : Observable<MatchedTogglJobCounts> {
        return this._http.post<MatchedTogglJobCounts>(this.originUrl + 'api/toggl/update-toggle-job-ids', null);
    }

    loadTimeEntries(start: Date, end: Date) : Observable<LoadTimeEntriesResult> {
        return this._http.post<LoadTimeEntriesResult>(this.originUrl + `api/toggl/load-time-entries?start=${this.format(start)}&end=${this.format(end)}`, null);
    }
    aggregateTimeEntries(start: Date, end: Date) : Observable<LoadTimeEntriesResult> {
        return this._http.post<LoadTimeEntriesResult>(this.originUrl + `api/toggl/aggregate-time-entries?start=${this.format(start)}&end=${this.format(end)}`, null);
    }

    updateTimeEntryRates(start: Date, end: Date) : Observable<UpdateTimeEntryRatesResult> {
        return this._http.post<UpdateTimeEntryRatesResult>(this.originUrl + `api/toggl/update-time-entry-rates?start=${this.format(start)}&end=${this.format(end)}`, null);
    }

    getTimeCalendar(year: number) : Observable<TimeCalendarResult> {
        return this._http.get<TimeCalendarResult>(this.originUrl + `api/toggl/get-time-calendar?year=${year}`)
        .pipe(map((r) => {
            if (r.success) {
                r.calendar.dayCounts.forEach((dc) => {
                    dc.day = new Date(dc.day);
                });
            }
            return r;
        }));
    }

    // add a method called logout which sets toggluser to null and removes it from local storage
    logout() {
        this.toggluser = null;
        window.localStorage.removeItem('toggluser');
        this._isLoggedIn.next(false);
        if (this.timerSubscription) {
            this.stopTimerSubscription();
            this._togglRunningJob$.next(null);
            this.isTogglTimerRunning = false;
            this.timerSubscription = null;
        }
    }

    format(date: Date) {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }
}